import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { appDate, appWeekday } from '../../../../utils/appDateFormat';

const CorrectionRequestReviewModal = ({
  open,
  record,
  onClose,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation();
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (!open) return;
    setRejectionReason('');
  }, [open, record?.id]);

  if (!open || !record?.correctionRequest) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-foreground">{t('Correction Request Review')}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Employee Info */}
        <div className="bg-muted/20 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-foreground mb-2">{t('Employee Information')}</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">{t('Name')}:</span>
              <span className="ms-2 text-foreground">{record?.employee}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('Title')}:</span>
              <span className="ms-2 text-foreground">{record?.employeeTitle || '-'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('Department')}:</span>
              <span className="ms-2 text-foreground">{record?.department}</span>
            </div>
            <div>
              <span className="text-muted-foreground">{t('Date')}:</span>
              <span className="ms-2 text-foreground">
                {`${appWeekday(new Date(record?.correctionRequest?.date))}, ${appDate(new Date(record?.correctionRequest?.date))}`}
              </span>
            </div>
          </div>
        </div>

        {/* Request Details */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('Correction Type')}</label>
            <div className="p-3 bg-background border border-border rounded-lg">
              <span className="text-sm text-foreground">{record?.correctionRequest?.correctionType}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('Detailed Reason')}</label>
            <div className="p-3 bg-background border border-border rounded-lg">
              <p className="text-sm text-foreground leading-relaxed">{record?.correctionRequest?.detailedReason}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('Supporting Documents')}</label>
            <div className="space-y-2">
              {record?.correctionRequest?.supportingDocuments?.map((doc, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-background border border-border rounded-lg">
                  <Icon name="FileText" size={16} className="text-blue-500" />
                  <span className="text-sm text-foreground">{doc}</span>
                  <button type="button" className="ms-auto text-blue-500 hover:text-blue-700 text-sm">
                    {t('View')}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Current vs Requested Times */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('Current Times')}</label>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="text-sm">
                  <div>{t('Clock In')}: {record?.clockIn || t('N/A')}</div>
                  <div>{t('Clock Out')}: {record?.clockOut || t('N/A')}</div>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">{t('Requested Times')}</label>
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-sm">
                  <div>{t('Clock In')}: {record?.correctionRequest?.requestedClockIn || record?.clockIn || t('N/A')}</div>
                  <div>{t('Clock Out')}: {record?.correctionRequest?.requestedClockOut || record?.clockOut || t('N/A')}</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t('Rejection Reason (Optional)')}</label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e?.target?.value)}
              placeholder={t('Enter reason for rejection if you choose to reject...')}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={3}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors"
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            onClick={() => onReject?.(rejectionReason)}
            className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            {t('Reject Request')}
          </button>
          <button
            type="button"
            onClick={() => onApprove?.()}
            className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            {t('Approve Request')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CorrectionRequestReviewModal;
